import { useInfiniteQuery } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";
import { useMemo } from "react";

import { WhatsAppConversationsListResponse } from "../../aiConversations.types";
import { AI_CONVERSATIONS_QUERY_KEY } from "../../constants/enums";
import { requestGetConversations } from "./getConversations.request";
import { GetConversationsParams } from "./getConversations.types";

const DEFAULT_TAKE = 30;

type Filters = Omit<GetConversationsParams, "page">;

const useGetConversations = (filters: Filters = {}) => {
  const take = filters.take ?? DEFAULT_TAKE;

  const query = useInfiniteQuery<
    WhatsAppConversationsListResponse,
    ApiServiceError<ApiError>
  >({
    queryKey: [AI_CONVERSATIONS_QUERY_KEY, "list", { ...filters, take }],
    queryFn: async ({ pageParam = 1 }) =>
      await requestGetConversations({ ...filters, take, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.count / lastPage.take);
      const nextPage = lastPage.page + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    onError: toastServerError
  });

  const conversations = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  );

  return {
    conversations,
    count: query.data?.pages[0]?.count,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
};

export default useGetConversations;
